import { useEffect, useRef, useState } from 'react'
import { useLang, type Lang } from '../i18n'
import s from './QuestionBubbles.module.css'

const PHRASES: Record<Lang, string[]> = {
  en: [
    'Can you explain photosynthesis?',
    'Why is the sky blue?',
    'How do black holes form?',
    "What's the difference between DNA and RNA?",
    'How do vaccines actually work?',
    'Walk me through the water cycle',
    'Why do we have seasons?',
    'What causes earthquakes?',
    'How does gravity really work?',
    'Explain the second law of thermodynamics',
    'Help me with this quadratic equation',
    'Why is the derivative of sine cosine?',
    'What is the Pythagorean theorem?',
    'Can you explain logarithms?',
    "I'm stuck on this calculus problem",
    "What's the chain rule again?",
    'Why does 0.999… equal 1?',
    'Teach me integration by parts',
    "What's a determinant?",
    'Remind me how long division works',
    "What's a metaphor, really?",
    'Summarize chapter 3 of The Great Gatsby',
    'When do I use "affect" vs "effect"?',
    'Help me write a thesis statement',
    "What's iambic pentameter?",
    'Analyze this poem with me',
    'When did World War II actually start?',
    'Explain the French Revolution',
    'What caused the fall of Rome?',
    'Tell me about ancient Egypt',
    'What was the Renaissance about?',
    'Let me tell you what I learned today',
    "Quiz me on last week's material",
    "I don't understand this homework",
    'Can we review for my test tomorrow?',
    'Help me make flashcards',
    'How should I study for finals?',
    'I need help with my essay',
    'Walk me through this step by step',
    'What topics should I review?',
    'Give me some practice problems',
    "What's recursion?",
    'Explain time complexity',
    'How does a hash table work?',
    "What's a pointer in C?",
    'Why do leaves change color?',
    'What is inflation?',
    'How does the stock market work?',
    "What's climate change, exactly?",
    'How does the brain form memories?',
  ],
  ko: [
    '광합성을 설명해 줘',
    '하늘은 왜 파란색이야?',
    '블랙홀은 어떻게 생겨?',
    'DNA랑 RNA 차이가 뭐야?',
    '백신은 어떻게 작동해?',
    '물의 순환 다시 설명해 줘',
    '계절이 왜 생기는 거야?',
    '지진은 왜 일어나?',
    '중력은 실제로 어떻게 작용해?',
    '열역학 제2법칙 설명해 줘',
    '이 이차방정식 같이 풀자',
    '사인의 도함수가 왜 코사인이야?',
    '피타고라스 정리가 뭐였지?',
    '로그 개념 좀 설명해 줘',
    '이 미적분 문제에서 막혔어',
    '연쇄법칙 다시 알려줘',
    '왜 0.999…가 1이랑 같아?',
    '부분적분 가르쳐 줘',
    '행렬식이 뭐야?',
    '긴 나눗셈 다시 보여줘',
    '은유가 정확히 뭐야?',
    '위대한 개츠비 3장 요약해 줘',
    '"affect"랑 "effect" 언제 써?',
    '논제 문장 쓰는 거 도와줘',
    '약강 오보격이 뭐야?',
    '이 시 같이 분석해 보자',
    '제2차 세계대전 언제 시작했지?',
    '프랑스 혁명 설명해 줘',
    '로마는 왜 무너졌어?',
    '고대 이집트에 대해 알려줘',
    '르네상스가 뭐였어?',
    '오늘 수업에서 배운 거 말해 줄게',
    '지난주 내용으로 퀴즈 내 줘',
    '이 숙제 이해가 안 가',
    '내일 시험 대비 같이 복습하자',
    '암기카드 만들기 도와줘',
    '기말 공부는 어떻게 해야 돼?',
    '에세이 쓰는 거 도와줘',
    '단계별로 천천히 설명해 줘',
    '어떤 단원을 복습해야 할까?',
    '연습 문제 좀 내 줘',
    '재귀가 뭐야?',
    '시간복잡도 설명해 줘',
    '해시 테이블은 어떻게 작동해?',
    'C에서 포인터가 뭐야?',
    '나뭇잎은 왜 색이 변해?',
    '인플레이션이 뭐야?',
    '주식 시장은 어떻게 돌아가?',
    '기후변화가 정확히 뭐야?',
    '뇌는 기억을 어떻게 만들어?',
  ],
}

type Bubble = {
  id: number
  text: string
  xPx: number
  startYPx: number // distance from viewport bottom at spawn
  travelPx: number
  durationMs: number
  bornAt: number
  widthPx: number
  heightPx: number
}

const TARGET_COUNT = 12
const DURATION_MIN_MS = 7000
const DURATION_MAX_MS = 14000
const TRAVEL_FRACTION = 0.2
const SPAWN_INTERVAL_MS = 300
// On each spawn we sample this many random positions, discard ones that
// overlap a live bubble, and pick the candidate that sits farthest from
// the nearest live bubble's center — a soft bias toward empty areas.
const SPAWN_CANDIDATES = 10
const COLLISION_GAP_PX = 16
// Pre-measurement estimates. Overwritten with actual getBoundingClientRect
// values in the bubble's ref callback after it mounts.
const EST_HEIGHT_PX = 40
const EST_CHAR_WIDTH_PX = 9
const EST_PADDING_PX = 48

const rand = (min: number, max: number) => min + Math.random() * (max - min)

function estimateWidth(text: string, maxPx: number): number {
  return Math.min(text.length * EST_CHAR_WIDTH_PX + EST_PADDING_PX, maxPx)
}

function currentY(b: Bubble, now: number): number {
  const progress = Math.max(0, Math.min(1, (now - b.bornAt) / b.durationMs))
  return b.startYPx + progress * b.travelPx
}

function opacityAt(progress: number): number {
  const FADE_IN = 0.35
  const FADE_OUT_START = 0.7
  const MAX = 0.55
  if (progress <= 0 || progress >= 1) return 0
  if (progress < FADE_IN) return (progress / FADE_IN) * MAX
  if (progress > FADE_OUT_START)
    return (1 - (progress - FADE_OUT_START) / (1 - FADE_OUT_START)) * MAX
  return MAX
}

type Rect = { x: number; y: number; w: number; h: number }
function overlaps(a: Rect, b: Rect, gap: number): boolean {
  return !(
    a.x + a.w + gap < b.x ||
    b.x + b.w + gap < a.x ||
    a.y + a.h + gap < b.y ||
    b.y + b.h + gap < a.y
  )
}

// Horizontal and vertical soft-edge widths for the center keep-out fade.
const SAFE_FADE_PX = 60
const SAFE_HORIZ_PAD_PX = 12

// Returns a 0..1 multiplier that fades a bubble out as it crosses the
// logo/buttons rect and back in once it's clear. Outside the safe column
// it's always 1 — bubbles on the left/right margins are unaffected.
function centerMask(
  xPx: number,
  widthPx: number,
  yFromBottom: number,
  heightPx: number,
  safe: DOMRect | null,
  vh: number,
): number {
  if (!safe) return 1
  const bubbleLeft = xPx
  const bubbleRight = xPx + widthPx
  if (
    bubbleRight + SAFE_HORIZ_PAD_PX < safe.left ||
    bubbleLeft - SAFE_HORIZ_PAD_PX > safe.right
  ) {
    return 1
  }
  const bubbleCenterY = vh - yFromBottom - heightPx / 2
  if (bubbleCenterY >= safe.top && bubbleCenterY <= safe.bottom) return 0
  if (bubbleCenterY > safe.bottom) {
    // Below the safe zone, moving up into it.
    const d = bubbleCenterY - safe.bottom
    return d >= SAFE_FADE_PX ? 1 : d / SAFE_FADE_PX
  }
  // Above the safe zone, having passed it.
  const d = safe.top - bubbleCenterY
  return d >= SAFE_FADE_PX ? 1 : d / SAFE_FADE_PX
}

export default function QuestionBubbles() {
  const lang = useLang()
  const phrases = PHRASES[lang]

  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const bubblesRef = useRef<Bubble[]>([])
  // The animation loop reads ``bubblesRef.current`` synchronously
  // inside its rAF callback (see below). Mirroring state to a ref
  // during render is the canonical pattern for "always-current value
  // accessible from a non-React closure"; React 19's lint rule warns
  // because writing a ref during render is technically a side effect,
  // but the alternative (useEffect to sync) introduces a one-frame
  // lag that visibly degrades the animation. Documented exception.
  // eslint-disable-next-line react-hooks/refs
  bubblesRef.current = bubbles

  const nodeRefs = useRef<Map<number, HTMLSpanElement>>(new Map())
  const nextIdRef = useRef(1)
  const safeElRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    safeElRef.current = document.querySelector<HTMLElement>('.splash-center')
    return () => {
      safeElRef.current = null
    }
  }, [])

  // Reset on language change so we don't carry old-language pills
  // over. Could also be expressed as ``key={lang}`` on the parent
  // mount, but SplashScreen explicitly chose NOT to remount this
  // component (see SplashScreen.tsx header comment) — remounting
  // restarts the animation loop and produces a visible jump. The
  // in-place reset preserves the rAF schedule.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBubbles([])
    nodeRefs.current.clear()
  }, [lang])

  useEffect(() => {
    let rafId = 0
    let lastSpawnAt = 0

    const tick = () => {
      const now = performance.now()
      const list = bubblesRef.current

      // Reap dead bubbles.
      const alive = list.filter((b) => now - b.bornAt < b.durationMs)
      let changed = alive.length !== list.length

      // Spawn if under target.
      if (
        now - lastSpawnAt > SPAWN_INTERVAL_MS &&
        alive.length < TARGET_COUNT
      ) {
        lastSpawnAt = now
        const w = window.innerWidth
        const h = window.innerHeight
        const text = phrases[Math.floor(Math.random() * phrases.length)]
        const widthPx = estimateWidth(text, Math.min(w * 0.6, 480))
        const heightPx = EST_HEIGHT_PX
        const travelPx = h * TRAVEL_FRACTION
        const durationMs = rand(DURATION_MIN_MS, DURATION_MAX_MS)

        const maxStartY = Math.max(0, h - heightPx - travelPx - 16)
        const maxX = Math.max(16, w - widthPx - 16)

        // Snapshot current rects of live bubbles for both the collision
        // test and the spread score.
        const liveRects = alive.map((other) => ({
          x: other.xPx,
          y: currentY(other, now),
          w: other.widthPx,
          h: other.heightPx,
        }))

        let best: Rect | null = null
        let bestScore = -Infinity
        for (let i = 0; i < SPAWN_CANDIDATES; i++) {
          const candidate: Rect = {
            x: rand(16, maxX),
            y: rand(16, Math.max(16, maxStartY)),
            w: widthPx,
            h: heightPx,
          }
          if (
            liveRects.some((r) => overlaps(candidate, r, COLLISION_GAP_PX))
          ) {
            continue
          }
          // Score = distance from candidate's center to the nearest live
          // bubble's center. With no live bubbles, every candidate ties at
          // Infinity and we keep the first one (fully random).
          const cx = candidate.x + candidate.w / 2
          const cy = candidate.y + candidate.h / 2
          let minDist = Infinity
          for (const r of liveRects) {
            const d = Math.hypot(
              cx - (r.x + r.w / 2),
              cy - (r.y + r.h / 2),
            )
            if (d < minDist) minDist = d
          }
          if (minDist > bestScore) {
            bestScore = minDist
            best = candidate
          }
        }

        if (best) {
          alive.push({
            id: nextIdRef.current++,
            text,
            xPx: best.x,
            startYPx: best.y,
            travelPx,
            durationMs,
            bornAt: now,
            widthPx,
            heightPx,
          })
          changed = true
        }
      }

      if (changed) {
        bubblesRef.current = alive
        setBubbles(alive)
      }

      // Drive each bubble's transform + opacity directly on the DOM node,
      // avoiding a React re-render per frame.
      const safeRect = safeElRef.current?.getBoundingClientRect() ?? null
      const vh = window.innerHeight
      for (const b of bubblesRef.current) {
        const el = nodeRefs.current.get(b.id)
        if (!el) continue
        const progress = (now - b.bornAt) / b.durationMs
        const y = b.startYPx + progress * b.travelPx
        el.style.transform = `translate3d(${b.xPx}px, ${-y}px, 0)`
        const mask = centerMask(b.xPx, b.widthPx, y, b.heightPx, safeRect, vh)
        el.style.opacity = String(opacityAt(progress) * mask)
      }

      rafId = requestAnimationFrame(tick)
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [phrases])

  return (
    // ``data-question-bubbles`` is the stable hook for the visual-
    // regression helper (``e2e/visual/_helpers.ts``) — under CSS
    // Modules the class name is hashed, so the helper can't pin the
    // hide-rule via class. The attribute is API-shaped and survives
    // the migration.
    <div className={s.questionBubbles} data-question-bubbles aria-hidden="true">
      {bubbles.map((b) => (
        <span
          key={b.id}
          className={s.questionBubble}
          ref={(el) => {
            if (el) {
              nodeRefs.current.set(b.id, el)
              // Replace the estimate with the real measured size so
              // collision checks for future spawns are accurate.
              const rect = el.getBoundingClientRect()
              b.widthPx = rect.width
              b.heightPx = rect.height
            } else {
              nodeRefs.current.delete(b.id)
            }
          }}
        >
          {b.text}
        </span>
      ))}
    </div>
  )
}
